import { useLayoutEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Button, Dropdown, Icon, Spin, Tag, Tooltip, TruncatedText, Typography } from '@bookora/ui';
import type { MenuProps } from '@bookora/ui';
import { useNavigate } from 'react-router-dom';

import { fetchMe, switchClinic } from '@/api/endpoints/auth';
import type { Membership } from '@/api/types';
import { toast } from '@/lib/feedback';

import { identityFromMe, useAuth, useSessionStore } from './token-store';

/**
 * Switches the token to another clinic this account belongs to.
 *
 * ── WHY THE CACHE IS CLEARED, NOT INVALIDATED ───────────────────────────────
 *
 * A clinic switch changes WHOSE data the panel is holding. Invalidation would
 * leave clinic A's patient rows in memory and serve them from cache while the
 * refetch for clinic B is in flight — a cross-tenant leak visible for as long
 * as a slow request takes. `queryClient.clear()` drops everything. Query keys
 * are also clinic-scoped (`api/query-keys.ts`), so this is the second of two
 * independent guards on the same failure.
 *
 * The server is the authority here too: `POST /auth/switch-clinic` resolves the
 * membership on (userId, clinicId) together, so a forged clinic id widens
 * nothing — the worst outcome is a 403.
 */
/**
 * `full`    — the header on a wide screen: glyph + the clinic's name.
 * `compact` — the header on a phone: the name alone, truncated harder. It used
 *             to be the glyph alone, and a person working at two clinics could
 *             not tell from their phone which clinic they were booking into.
 * `block`   — the mobile drawer: a full-width, labelled «تغییر کلینیک».
 */
export type ClinicSwitcherVariant = 'full' | 'compact' | 'block';

/**
 * Whether the element's single line of text is cut by its box, kept current
 * as the header resizes. jsdom has no layout and always answers `false`.
 *
 * Takes the element itself (a callback ref's state), not a ref object: the
 * span mounts later than the hook runs whenever the switcher first rendered
 * nothing (one membership) or the block variant, and an effect keyed on a
 * ref object would never see it arrive.
 */
function useIsTruncated(el: HTMLElement | null, text: string): boolean {
  const [truncated, setTruncated] = useState(false);
  useLayoutEffect(() => {
    if (!el) {
      setTruncated(false);
      return;
    }
    const measure = (): void => setTruncated(el.scrollWidth > el.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el, text]);
  return truncated;
}

export function ClinicSwitcher({ variant = 'full' }: { variant?: ClinicSwitcherVariant }) {
  const { memberships, activeClinic, activeClinicId } = useAuth();
  const setSession = useSessionStore((state) => state.setSession);
  const setIdentity = useSessionStore((state) => state.setIdentity);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);
  const [nameEl, setNameEl] = useState<HTMLSpanElement | null>(null);
  const clinicName = activeClinic?.clinicName ?? 'انتخاب کلینیک';
  const nameCut = useIsTruncated(nameEl, clinicName);

  if (memberships.length <= 1) return null;

  async function handleSwitch(clinicId: string): Promise<void> {
    if (clinicId === activeClinicId || switching) return;
    setSwitching(true);
    try {
      const result = await switchClinic(clinicId);
      setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresIn: result.expiresIn,
        sessionId: result.sessionId,
        user: result.user,
        activeClinicId: result.activeClinicId ?? clinicId,
        memberships: result.memberships ?? memberships,
      });

      queryClient.clear();

      // The permission set is per-membership: the same person can be an owner
      // in one clinic and a nurse in another.
      const me = await fetchMe();
      setIdentity(identityFromMe(me, clinicId));

      // Back to the dashboard: the record the user was looking at belongs to
      // the previous clinic and its id resolves to nothing here.
      void navigate('/', { replace: true });
      toast.success(
        `به ${me.memberships?.find((m) => m.clinicId === clinicId)?.clinicName ?? 'کلینیک انتخاب‌شده'} منتقل شدید`,
      );
    } catch (error) {
      toast.error(error);
    } finally {
      setSwitching(false);
    }
  }

  const items: MenuProps['items'] = memberships.map((membership) => ({
    key: membership.clinicId,
    label: <ClinicOption membership={membership} active={membership.clinicId === activeClinicId} />,
    disabled: membership.clinicStatus === 'CLOSED',
    // What the row's status tag means (and, for a closed clinic, why the row
    // cannot be picked), on the row itself: the menu item is what a keyboard
    // or screen-reader user lands on, and the tag's tooltip is pointer-only.
    // rc-menu forwards unknown item props to the `<li>`. Not `title`: AntD
    // copies a string title onto the `<li>` as a native browser tooltip.
    'aria-description': membershipNote(membership) ?? undefined,
  }));

  return (
    <Dropdown
      menu={{
        items,
        onClick: ({ key }) => void handleSwitch(key),
        selectedKeys: activeClinicId ? [activeClinicId] : [],
      }}
      trigger={['click']}
      // `bottom-start`: this control sits at the reading-start end of the
      // header, so the menu hangs from the same edge. It used to say
      // `bottomLeft`, which in RTL is the OPPOSITE edge — a literal physical
      // side written by LTR habit. The kit no longer accepts one.
      placement="bottom-start"
      disabled={switching}
    >
      <Button
        type="text"
        block={variant === 'block'}
        className={variant === 'block' ? 'bk-clinic-switch--block' : undefined}
        // The spinner sits in a slot the size of the icon it replaces: a bare
        // small Spin is ~15px against the icon's 24, so the button narrowed
        // and the clinic name slid 8.6px the moment a switch started.
        icon={
          switching ? (
            <span className="bk-icon-slot">
              <Spin size="small" />
            </span>
          ) : variant === 'compact' ? null : (
            <Icon name="switch" />
          )
        }
        // The clinic's name is part of the control's name, not only its text:
        // the label alone told a screen reader what the button does but not
        // which clinic is active now.
        aria-label={`تغییر کلینیک فعال: ${clinicName}`}
        aria-busy={switching}
        // A long name is cut to fit the header; the full one is the button's
        // own hint, only while it is actually cut. The button's hint, not a
        // Tooltip nested inside the Dropdown's trigger (two overlays on one
        // control), and not `TruncatedText` (a Tooltip of its own).
        hint={variant !== 'block' && nameCut ? clinicName : undefined}
      >
        {variant === 'block' ? (
          'تغییر کلینیک'
        ) : (
          <span
            ref={setNameEl}
            className="bk-truncate"
            style={{ maxInlineSize: variant === 'compact' ? '40vw' : 'min(220px, 32vw)' }}
          >
            {clinicName}
          </span>
        )}
        {variant === 'compact' ? <Icon name="chevron-down" size={16} /> : null}
      </Button>
    </Dropdown>
  );
}

/**
 * A trial that ran out (or an ACTIVE clinic whose period ended) is still
 * TRIAL/ACTIVE in `clinicStatus`; `access` from `/auth/me` is what says it can
 * no longer save.
 */
function endedWithoutStatus(membership: Membership): boolean {
  return (
    membership.access?.writesAllowed === false &&
    (membership.clinicStatus === 'TRIAL' || membership.clinicStatus === 'ACTIVE')
  );
}

const SUSPENDED_NOTE =
  'ثبت اطلاعات جدید در این کلینیک موقتاً غیرفعال است؛ همچنان می‌تونید پرونده‌ها رو ببینید.';
const PAST_DUE_NOTE = 'صورت‌حساب اشتراک این کلینیک پرداخت نشده.';
const CLOSED_NOTE = 'این کلینیک بسته شده. برای بازگشایی اون با پشتیبانی بوکورا تماس بگیرید.';

/** The sentence(s) explaining a membership row's status tags, or `null` when there are none. */
function membershipNote(membership: Membership): string | null {
  const notes: string[] = [];
  if (endedWithoutStatus(membership) && membership.access?.readOnlyReasonFa) {
    notes.push(membership.access.readOnlyReasonFa);
  }
  if (membership.clinicStatus === 'SUSPENDED') notes.push(SUSPENDED_NOTE);
  if (membership.clinicStatus === 'PAST_DUE') notes.push(PAST_DUE_NOTE);
  if (membership.clinicStatus === 'CLOSED') notes.push(CLOSED_NOTE);
  return notes.length > 0 ? notes.join(' ') : null;
}

/**
 * A status tag inside a menu row, with its meaning in a pointer tooltip. The
 * tag is not made focusable: it sits inside a `menuitem`, whose own
 * `aria-description` carries the same sentence for keyboard and screen-reader
 * users (see `membershipNote`).
 */
function NotedTag({
  note,
  color,
  children,
}: {
  note: string | null | undefined;
  color?: string;
  children: string;
}) {
  return (
    <Tooltip kind="description" title={note ?? undefined}>
      <Tag color={color}>{children}</Tag>
    </Tooltip>
  );
}

function ClinicOption({ membership, active }: { membership: Membership; active: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--bk-space-2)',
        minInlineSize: 220,
        maxInlineSize: 320,
      }}
    >
      <div style={{ flex: 1, minInlineSize: 0 }}>
        <TruncatedText as="div">{membership.clinicName}</TruncatedText>
        <div className="bk-subtle" style={{ fontSize: 'var(--bk-text-caption)' }}>
          {/* «،», not «·»: beside Persian digits a middle dot reads as «۰». */}
          {membership.roleName ?? 'بدون نقش'}
          {membership.isDoctor ? '، پزشک' : ''}
        </div>
      </div>
      {/* Shown before the switch, so nobody lands in a read-only clinic by
          surprise. */}
      {endedWithoutStatus(membership) ? (
        <NotedTag color="warning" note={membership.access?.readOnlyReasonFa}>
          فقط قابل مشاهده
        </NotedTag>
      ) : membership.clinicStatus === 'TRIAL' ? (
        <Tag>آزمایشی</Tag>
      ) : null}
      {membership.clinicStatus === 'SUSPENDED' ? (
        <NotedTag color="warning" note={SUSPENDED_NOTE}>
          موقتاً غیرفعال
        </NotedTag>
      ) : null}
      {/* Not «بدهی»: an invoice waiting to be paid, not an accusation. */}
      {membership.clinicStatus === 'PAST_DUE' ? (
        <NotedTag color="warning" note={PAST_DUE_NOTE}>
          در انتظار پرداخت
        </NotedTag>
      ) : null}
      {membership.clinicStatus === 'CLOSED' ? (
        <NotedTag note={CLOSED_NOTE}>بسته‌شده</NotedTag>
      ) : null}
      {active ? <Icon name="check" label="کلینیک فعال" /> : null}
    </div>
  );
}

/** Read-only clinic name for the top bar, when there is nothing to switch to. */
export function ActiveClinicLabel() {
  const { activeClinic } = useAuth();
  if (!activeClinic) return null;

  // The full name in a tooltip only when the header actually cuts it.
  return (
    <Typography.Text
      strong
      style={{ display: 'flex', minInlineSize: 0, maxInlineSize: 'min(320px, 100%)' }}
    >
      <TruncatedText>{activeClinic.clinicName}</TruncatedText>
    </Typography.Text>
  );
}
